import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Brain,
  CheckCircle2,
  ChevronRight,
  Eye,
  Link2,
  Lock,
  RefreshCcw,
  Send,
  Shield,
  ShieldAlert,
  Wifi,
  X,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { createRequest } from '@/features/client-requests/api';

// ── Per-product static marketing content ─────────────────────────────────────

interface ProductContent {
  displayName: string;
  tagline: string;
  shortDescription: string;
  category: string;
  icon: React.ReactNode;
  accentColor: string;
  glowColor: string;
  features: { title: string; description: string }[];
  useCases: string[];
  whyTeamsUse: string;
  networkBadge: string;
}

const PRODUCT_CONTENT: Record<string, ProductContent> = {
  SIGNAL: {
    displayName: 'Authmatech Target',
    tagline: 'Mobile Identity Targeting Intelligence',
    shortDescription:
      'Build recovery audiences, identify high-intent users, and unlock CRM targeting intelligence — powered by your verification and identity journey data.',
    category: 'Targeting Intelligence',
    icon: <Activity className="h-8 w-8" />,
    accentColor: 'from-violet-500 to-purple-600',
    glowColor: 'shadow-[0_0_60px_rgba(139,92,246,0.25)]',
    features: [
      {
        title: 'Recovery Audience Builder',
        description: 'Automatically surface users who failed verification and build retargeting audiences from real journey data.',
      },
      {
        title: 'Unresolved Journey Intelligence',
        description: 'Identify users still stuck in failed verification loops and turn them into actionable recovery campaigns.',
      },
      {
        title: 'CRM Targeting Intelligence',
        description: 'Layer Authmatech identity signals on top of your CRM records to prioritize outreach by recovery likelihood.',
      },
      {
        title: 'Onboarding Conversion Intelligence',
        description: 'Track drop-off points across your onboarding funnel and surface high-intent users who are ready to convert.',
      },
    ],
    useCases: [
      'Build audiences of recoverable users from failed verification journeys',
      'Target unresolved onboarding drop-offs before they churn permanently',
      'Match CRM records with Authmatech identity intelligence for precision targeting',
      'Re-engage users who recovered previously but are now failing again',
    ],
    whyTeamsUse:
      'Target turns your verification data into a growth engine. Every failed or recovered journey becomes an actionable audience signal — insights that no third-party targeting tool can replicate.',
    networkBadge: 'Powered by identity journey intelligence',
  },

  SHIELD: {
    displayName: 'Authmatech Shield',
    tagline: 'Enterprise Fraud Detection & Risk Protection',
    shortDescription:
      'Telecom-native fraud signals that detect SIM swaps, number porting, and account takeover attempts before they happen.',
    category: 'Fraud Protection',
    icon: <Shield className="h-8 w-8" />,
    accentColor: 'from-emerald-500 to-teal-600',
    glowColor: 'shadow-[0_0_60px_rgba(16,185,129,0.25)]',
    features: [
      {
        title: 'SIM Swap Detection',
        description: 'Detect SIM card changes in real time — a primary vector for account takeover fraud.',
      },
      {
        title: 'Number Porting Alerts',
        description: 'Flag mobile number port-out events that signal fraudulent activity within milliseconds.',
      },
      {
        title: 'Device Fingerprinting',
        description: 'Correlate device identity with mobile network identity to surface mismatches.',
      },
      {
        title: 'Risk Scoring Engine',
        description: 'Composite fraud risk scores combining carrier signals, behavioral data, and history.',
      },
    ],
    useCases: [
      'Block account takeover attacks before credentials are compromised',
      'Reduce fraud losses with carrier-verified risk signals',
      'Auto-step-up authentication for high-risk identity events',
      'Meet regulatory requirements for fraud detection in financial services',
    ],
    whyTeamsUse:
      'Shield uses telecom-layer signals that third-party fraud tools cannot access. SIM swap and port-out detection operates at the carrier level — milliseconds after an event occurs, your systems know.',
    networkBadge: 'Carrier-level fraud intelligence',
  },

  TRUST: {
    displayName: 'Authmatech Trust',
    tagline: 'Unified Trust Scoring for Every Customer Interaction',
    shortDescription:
      'Generate a unified mobile identity trust score from verification history, behavior, and network intelligence to power faster allow, review, or block decisions.',
    category: 'Risk Scoring',
    icon: <Eye className="h-8 w-8" />,
    accentColor: 'from-cyan-500 to-sky-600',
    glowColor: 'shadow-[0_0_60px_rgba(6,182,212,0.25)]',
    features: [
      {
        title: 'Composite Trust Scores',
        description: 'Multi-dimensional trust scores that combine mobile, behavioral, and network identity signals.',
      },
      {
        title: 'Identity Continuity',
        description: "Track whether a user's mobile identity has remained consistent since onboarding.",
      },
      {
        title: 'Adaptive Authentication',
        description: 'Risk-stratified step-up prompts — only challenge users when trust drops below threshold.',
      },
      {
        title: 'Historical Pattern Analysis',
        description: 'Learn individual user baselines and flag deviations across sessions.',
      },
    ],
    useCases: [
      'Reduce friction for trusted users while stepping up suspicious ones',
      'Power risk-tiered onboarding flows for financial services',
      'Continuously monitor existing customers for identity drift',
      'Replace static authentication rules with dynamic trust decisions',
    ],
    whyTeamsUse:
      'Trust enables risk-stratified decisions without user friction. High-trust users move through your flows unimpeded; low-trust sessions get challenged. All powered by mobile network data your competitors cannot access.',
    networkBadge: 'Mobile identity continuity engine',
  },

  STUCK_PLUS: {
    displayName: 'Authmatech Stuck+',
    tagline: 'Unresolved Journey Intelligence & Identity Recovery',
    shortDescription:
      'Identify users who failed verification and remain unresolved. Prioritize and recover lost onboarding users before they churn permanently.',
    category: 'Recovery Intelligence',
    icon: <RefreshCcw className="h-8 w-8" />,
    accentColor: 'from-amber-500 to-orange-600',
    glowColor: 'shadow-[0_0_60px_rgba(245,158,11,0.25)]',
    features: [
      {
        title: 'Unresolved Journey Detection',
        description: 'Surface users who failed mobile verification and have not yet recovered — at any scale.',
      },
      {
        title: 'Attempt History & Insights',
        description: 'See how many times each user failed, when they first failed, and the full verification timeline.',
      },
      {
        title: 'Secure Identity Reveal',
        description: 'Admin-authorized mobile reveal for unresolved sessions — credit-gated, audited, and privacy-safe.',
      },
      {
        title: 'Recovery Audience Intelligence',
        description: 'Feed unresolved journeys into Connect and Target to power CRM recovery and retargeting campaigns.',
      },
    ],
    useCases: [
      'Identify and prioritize users who failed onboarding verification',
      'Reduce onboarding drop-off by acting on unresolved identity journeys',
      'Use Secure Reveal to recover high-value users blocked by verification loops',
      'Feed unresolved users into CRM and targeting workflows via Connect and Target',
    ],
    whyTeamsUse:
      "Stuck+ surfaces the users your funnel loses silently. Every unresolved verification journey is a potential customer waiting to be recovered — Stuck+ gives you the intelligence to act on them.",
    networkBadge: 'Unresolved mobile identity intelligence',
  },

  CONNECT: {
    displayName: 'Authmatech Connect',
    tagline: 'CRM Identity Intelligence & Synchronization',
    shortDescription:
      'Synchronize your CRM with Authmatech identity journey intelligence — match customers to verification outcomes, Stuck/Stuck+ journeys, and recovery intelligence.',
    category: 'CRM Intelligence',
    icon: <Link2 className="h-8 w-8" />,
    accentColor: 'from-blue-500 to-indigo-600',
    glowColor: 'shadow-[0_0_60px_rgba(59,130,246,0.25)]',
    features: [
      {
        title: 'Real-time Verification Sync',
        description: 'Push verification events — matched, not matched, Stuck, Stuck+ — to your CRM, webhook, or custom API in real time.',
      },
      {
        title: 'Journey Intelligence on Profiles',
        description: 'Each CRM identity profile shows journey type (Stuck/Stuck+), current state (Recovered/Unresolved), and a plain-language insight.',
      },
      {
        title: 'Bulk Identity Import',
        description: 'Upload existing customer lists and match them against Authmatech identity intelligence for instant CRM enrichment.',
      },
      {
        title: 'Privacy-first by design',
        description: 'All synced identities use masked mobile numbers. Full reveal is only available via Stuck+ Secure Reveal.',
      },
    ],
    useCases: [
      'Sync verification outcomes and journey intelligence directly into your CRM',
      'Identify which CRM customers are in unresolved identity journeys',
      'Enrich leads with Stuck/Stuck+ intelligence before outreach',
      'Build recovery-targeted campaigns from CRM identity profiles',
    ],
    whyTeamsUse:
      'Connect bridges your CRM and Authmatech identity intelligence. Every customer record gets enriched with verification history, journey state, and recovery insights — transforming your CRM from a static database into a live identity intelligence platform.',
    networkBadge: 'CRM identity intelligence synchronization',
  },

  DETECT: {
    displayName: 'Authmatech Detect',
    tagline: 'Real-time Risk Intelligence & Anomaly Detection',
    shortDescription:
      'Monitor anomalies, repeated failures, suspicious retry patterns, and identity risk signals before they become incidents.',
    category: 'Risk Intelligence',
    icon: <ShieldAlert className="h-8 w-8" />,
    accentColor: 'from-rose-500 to-pink-600',
    glowColor: 'shadow-[0_0_60px_rgba(239,68,68,0.25)]',
    features: [
      {
        title: 'Behavioral Anomaly Detection',
        description: 'Identify when user behavior deviates from established mobile identity baselines.',
      },
      {
        title: 'Velocity Checks',
        description: 'Detect rapid-fire API calls, credential stuffing, and bot-like verification patterns.',
      },
      {
        title: 'Pattern Analysis',
        description: 'Surface suspicious patterns across your entire user base in real time.',
      },
      {
        title: 'Alert Workflows',
        description: 'Trigger webhooks, step-up flows, or blocks based on configurable anomaly thresholds.',
      },
    ],
    useCases: [
      'Flag unusual verification patterns before they escalate',
      'Prevent credential stuffing and bot-driven account creation',
      'Protect high-value API endpoints from abuse',
      'Comply with security monitoring requirements in regulated industries',
    ],
    whyTeamsUse:
      'Detect operates at the mobile network layer — it sees attack patterns that WAFs and application-level tools miss. Network-level anomalies are caught before they reach your application.',
    networkBadge: 'Network-layer anomaly intelligence',
  },
};

const FALLBACK_CONTENT: ProductContent = {
  displayName: 'Authmatech Product',
  tagline: 'Advanced Mobile Identity Intelligence',
  shortDescription: 'Enterprise-grade mobile identity intelligence powered by carrier-level network data.',
  category: 'Mobile Intelligence',
  icon: <Brain className="h-8 w-8" />,
  accentColor: 'from-primary-500 to-blue-600',
  glowColor: 'shadow-[0_0_60px_rgba(37,99,235,0.25)]',
  features: [
    { title: 'Network Intelligence', description: 'Carrier-grade signals for every decision.' },
    { title: 'Real-time Processing', description: 'Sub-100ms response times at scale.' },
    { title: 'Enterprise Ready', description: 'SOC 2 compliant, SLA-backed infrastructure.' },
    { title: 'Simple Integration', description: 'REST API with SDK support.' },
  ],
  useCases: [
    'Reduce fraud and account takeover',
    'Improve conversion and onboarding',
    'Comply with identity verification regulations',
    'Enrich customer data with network-verified signals',
  ],
  whyTeamsUse: 'Mobile network intelligence that no third-party data provider can replicate.',
  networkBadge: 'Powered by mobile network intelligence',
};

// ── Request Access Modal ──────────────────────────────────────────────────────

function RequestAccessModal({
  productCode,
  productName,
  onClose,
  onSuccess,
}: {
  productCode: string;
  productName: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const [useCase, setUseCase] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createRequest({
        requestType: 'PRODUCT_ACCESS',
        requestedProductCode: productCode,
        description: useCase.trim() || `Request access to ${productName}`,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'requests'] });
      void qc.invalidateQueries({ queryKey: ['me', 'requests-unread'] });
      onSuccess();
    },
    onError: (err) => {
      setError(isApiError(err) ? err.firstErrorMessage : 'Failed to submit request. Please try again.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1829] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-white">Request Product Access</h2>
            <p className="mt-0.5 text-xs text-white/50">{productName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-white/30 transition-colors hover:bg-white/[0.07] hover:text-white/60"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-white/60">
            Tell us about your use case. Our team will review your request and reach out within 1–2 business days.
          </p>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-white/50">
              Use case <span className="text-white/30">(optional)</span>
            </label>
            <textarea
              rows={4}
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              placeholder="Describe how you plan to use this product — e.g. fraud detection for our onboarding flow, CRM enrichment for 500k customer records..."
              className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-white/25 outline-none focus:border-primary-500/60 focus:ring-2 focus:ring-primary-500/20 transition-colors"
            />
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-white/10 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <Send className="h-3.5 w-3.5" />
            Send request
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main locked product experience ────────────────────────────────────────────

export function LockedProductExperience({ productCode }: { productCode: string }) {
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const content = PRODUCT_CONTENT[productCode] ?? FALLBACK_CONTENT;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#050816]">
      {/* Hero ─────────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        {/* Ambient glow */}
        <div
          className={`pointer-events-none absolute inset-0 ${content.glowColor} opacity-30`}
          style={{
            background: `radial-gradient(ellipse 80% 50% at 50% 0%, rgba(59,130,246,0.15) 0%, transparent 70%)`,
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 pb-16 pt-14">
          {/* Lock badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5">
            <Lock className="h-3 w-3 text-white/40" />
            <span className="text-xs font-medium text-white/50">Available on request · {content.category}</span>
          </div>

          <div className="flex flex-col gap-10 lg:flex-row lg:items-start">
            {/* Left: Text */}
            <div className="flex-1">
              {/* Product icon */}
              <div
                className={`mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${content.accentColor} text-white shadow-lg`}
              >
                {content.icon}
              </div>

              <h1 className="text-4xl font-bold leading-tight tracking-tight text-white">
                {content.displayName}
              </h1>
              <p className="mt-2 text-lg font-medium text-white/50">{content.tagline}</p>
              <p className="mt-5 text-base leading-relaxed text-white/65">{content.shortDescription}</p>

              {submitted ? (
                <div className="mt-8 flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-5 py-4">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-300">Request sent to Authmatech</p>
                    <p className="mt-0.5 text-xs text-emerald-400/70">
                      Our team will review your request and reach out within 1–2 business days.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setShowModal(true)}
                    className={`inline-flex items-center gap-2 rounded-xl bg-gradient-to-r ${content.accentColor} px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl`}
                  >
                    <Lock className="h-4 w-4" />
                    Request Product Access
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-white/35">Response within 1–2 business days</p>
                </div>
              )}
            </div>

            {/* Right: Feature card grid */}
            <div className="w-full lg:w-[380px] shrink-0">
              <div className="grid grid-cols-1 gap-3">
                {content.features.map((f) => (
                  <div
                    key={f.title}
                    className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-4 backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${content.accentColor} opacity-80`}
                      >
                        <Zap className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{f.title}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-white/50">{f.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use Cases ────────────────────────────────────────────────────────── */}
      <div className="border-t border-white/[0.06]">
        <div className="mx-auto max-w-5xl px-6 py-14">
          <div className="grid gap-12 lg:grid-cols-2">
            {/* Use cases */}
            <div>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.14em] text-white/30">
                Use Cases
              </p>
              <ul className="space-y-3">
                {content.useCases.map((uc) => (
                  <li key={uc} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400/70" />
                    <span className="text-sm leading-relaxed text-white/65">{uc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Why teams use this */}
            <div>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.14em] text-white/30">
                Why Teams Choose This
              </p>
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-6">
                <p className="text-sm leading-relaxed text-white/65">{content.whyTeamsUse}</p>
              </div>

              {/* Network intelligence badge */}
              <div className="mt-6 flex items-center gap-2.5 rounded-xl border border-white/[0.07] bg-white/[0.03] px-4 py-3">
                <Wifi className={`h-4 w-4 bg-gradient-to-r ${content.accentColor} bg-clip-text`} style={{ color: 'transparent', filter: 'drop-shadow(0 0 6px rgba(59,130,246,0.5))' }} />
                <p className="text-xs font-medium text-white/50">{content.networkBadge}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA ───────────────────────────────────────────────────────── */}
      {!submitted && (
        <div className="border-t border-white/[0.06]">
          <div className="mx-auto max-w-5xl px-6 py-12 text-center">
            <p className="text-sm font-medium text-white/40">
              Ready to unlock {content.displayName} for your workspace?
            </p>
            <button
              onClick={() => setShowModal(true)}
              className={`mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r ${content.accentColor} px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:opacity-90`}
            >
              <Lock className="h-4 w-4" />
              Request Product Access
            </button>
            <p className="mt-3 text-xs text-white/25">
              Our team will reach out within 1–2 business days.
            </p>
          </div>
        </div>
      )}

      {showModal && (
        <RequestAccessModal
          productCode={productCode}
          productName={content.displayName}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setSubmitted(true);
          }}
        />
      )}
    </div>
  );
}
